import React, { useRef, useEffect } from 'react';

interface Props {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  onOpenMobileFilter: () => void;
  isStreaming: boolean;
  capExceeded: boolean;
  capMessage?: string;
  refineEnabled: boolean;
  onRefineToggle: (enabled: boolean) => void;
  isRefining: boolean;
  refineSlow?: boolean;
  refineNotice?: string | null;
}

export const ChatInput: React.FC<Props> = ({
  value,
  onChange,
  onSend,
  onOpenMobileFilter,
  isStreaming,
  capExceeded,
  capMessage,
  refineEnabled,
  onRefineToggle,
  isRefining,
  refineSlow,
  refineNotice,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!isStreaming) textareaRef.current?.focus({ preventScroll: true });
  }, [isStreaming]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isStreaming && !isRefining && !capExceeded) onSend();
    }
  };

  return (
    <div className="ai-input-area">
      {capExceeded && capMessage && (
        <div className="ai-cap-banner">{capMessage}</div>
      )}
      <div className="ai-chat-toolbar">
        <label
          className="ai-refine-toggle"
          title="Before answering, suggest sharper versions of your question and show how well each matches the library"
        >
          <input
            type="checkbox"
            checked={refineEnabled}
            onChange={e => onRefineToggle(e.target.checked)}
          />
          <span className="ai-refine-toggle-track"><span className="ai-refine-toggle-thumb" /></span>
          <span className="ai-refine-toggle-label">Refine my question</span>
        </label>
        {isRefining && (
          <span className="ai-refine-status ai-refine-status-busy">
            {refineSlow
              ? 'Still working… (the first request after a quiet spell can be slow)'
              : 'Finding sharper versions of your question…'}
          </span>
        )}
        {!isRefining && refineNotice && <span className="ai-refine-status ai-refine-status-notice" role="status">{refineNotice}</span>}
        <button className="ai-btn-toolbar ai-btn-filter" onClick={onOpenMobileFilter} title="Filter documents">
          ☰ Filter docs
        </button>
      </div>
      <div className="ai-chat-input-row">
        <textarea
          ref={textareaRef}
          className="ai-chat-textarea"
          placeholder={capExceeded ? 'Query limit reached' : 'Ask a question…'}
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isStreaming || isRefining || capExceeded}
          rows={2}
        />
        <button
          className="ai-btn-send"
          onClick={onSend}
          disabled={isStreaming || isRefining || capExceeded || !value.trim()}
          title="Send"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="white" />
          </svg>
        </button>
      </div>
      <p className="ai-chat-footer">
        AI uses selected documents and may contain errors.
      </p>
    </div>
  );
};
